// src/pages/ManagerConfiguracionAmenidades.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { collection, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { AMENITIES_CATALOG } from '../constants/amenidades';
import { useNotification } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { registerMovement } from '../utils/registerMovement';
import '../components/manager/AmenidadesConfigModal.css';
import './ManagerConfiguracionAmenidades.css';

const ALLOWED_ROLES = ['manager', 'superadmin', 'admin', 'platformadmin', 'admin_fracc'];

const ManagerConfiguracionAmenidades = ({ showBackButton = true }) => {
    const navigate = useNavigate();
    const { userData, currentUser } = useAuth();
    const { showNotification } = useNotification();
    const role = String(userData?.rol || '').toLowerCase();
    const canManage = ALLOWED_ROLES.includes(role);

    const [fraccionamientos, setFraccionamientos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list');
    const [selectedFraccionamiento, setSelectedFraccionamiento] = useState(null);
    const [selectedAmenities, setSelectedAmenities] = useState([]);
    const [saving, setSaving] = useState(false);

    const amenityLookup = useMemo(() => {
        const map = new Map();
        AMENITIES_CATALOG.forEach((item) => {
            map.set(item.id, item);
        });
        return map;
    }, []);

    useEffect(() => {
        if (!canManage) {
            setLoading(false);
            return undefined;
        }

        const ref = collection(db, 'fraccionamientos');
        const unsubscribe = onSnapshot(ref, (snapshot) => {
            const data = snapshot.docs.map((docSnap) => ({
                id: docSnap.id,
                ...docSnap.data(),
            }));
            setFraccionamientos(data);
            setLoading(false);
        }, (error) => {
            console.error('ManagerConfiguracionAmenidades: error al cargar fraccionamientos', error);
            showNotification('No se pudieron cargar los fraccionamientos.', 'error');
            setLoading(false);
        });

        return () => {
            unsubscribe();
        };
    }, [canManage, showNotification]);

    const handleOpenDetail = (fraccionamiento) => {
        setSelectedFraccionamiento(fraccionamiento);
        setSelectedAmenities([...(fraccionamiento.amenidadesActivas || [])]);
        setView('detail');
    };

    const handleToggleAmenity = (amenityId) => {
        setSelectedAmenities((prev) => (
            prev.includes(amenityId)
                ? prev.filter((id) => id !== amenityId)
                : [...prev, amenityId]
        ));
    };

    const handleSave = async () => {
        if (!selectedFraccionamiento) {
            return;
        }
        setSaving(true);
        try {
            const docRef = doc(db, 'fraccionamientos', selectedFraccionamiento.id);
            await updateDoc(docRef, {
                amenidadesActivas: selectedAmenities,
            });
            showNotification('Amenidades actualizadas correctamente.', 'success');
            try {
                await registerMovement({
                    fraccionamientoId: selectedFraccionamiento.id,
                    titulo: 'Configuración de amenidades actualizada',
                    descripcion: `Amenidades activas: ${selectedAmenities.length}`,
                    categoria: 'Configuración',
                    tipoMovimiento: 'amenidades_actualizadas',
                    origen: 'amenidades-config',
                    metadata: {
                        amenidadesActivas: selectedAmenities,
                        fraccionamientoNombre: selectedFraccionamiento.nombre || null,
                    },
                    userData,
                    currentUser,
                });
            } catch (logError) {
                console.error('ManagerConfiguracionAmenidades: error al registrar movimiento', logError);
            }
            handleCloseDetail();
        } catch (error) {
            console.error('ManagerConfiguracionAmenidades: error al guardar amenidades', error);
            showNotification('No se pudieron guardar los cambios. Intenta de nuevo.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleCloseDetail = () => {
        setView('list');
        setSelectedFraccionamiento(null);
        setSelectedAmenities([]);
    };

    const amenityTags = (ids = []) => {
        if (!ids.length) {
            return <span className="amenidades-config-tag muted">Sin amenidades configuradas</span>;
        }
        return ids.map((amenityId) => {
            const amenity = amenityLookup.get(amenityId);
            if (!amenity) {
                return null;
            }
            const Icon = amenity.Icon;
            return (
                <span key={amenityId} className="amenidades-config-tag">
                    <span className="amenidad-chip-icon"><Icon /></span>
                    {amenity.name}
                </span>
            );
        });
    };

    if (!canManage) {
        return (
            <div className="amenidades-config-page">
                <div className="amenidades-config-empty">
                    No tienes permisos para acceder a esta configuración.
                </div>
            </div>
        );
    }

    return (
        <div className="amenidades-config-page">
            <div className="amenidades-config-surface">
                {view === 'list' && (
                    <>
                        <div className="amenidades-config-header page">
                            <div className="amenidades-config-header-left">
                                {showBackButton && (
                                    <button
                                        type="button"
                                        className="amenidades-config-back"
                                        onClick={() => navigate(-1)}
                                        aria-label="Regresar"
                                    >
                                        <FaArrowLeft />
                                    </button>
                                )}
                                <div>
                                    <h2>Gestión de Amenidades</h2>
                                    <p>Selecciona un fraccionamiento para activar o desactivar las amenidades disponibles.</p>
                                </div>
                            </div>
                        </div>
                        <div className="amenidades-config-body">
                            {loading ? (
                                <p>Cargando fraccionamientos...</p>
                            ) : fraccionamientos.length === 0 ? (
                                <p>No hay fraccionamientos registrados.</p>
                            ) : (
                                <div className="amenidades-config-list">
                                    {fraccionamientos.map((fracc) => {
                                        const amenidades = Array.isArray(fracc.amenidadesActivas)
                                            ? fracc.amenidadesActivas
                                            : [];
                                        const count = amenidades.length;
                                        return (
                                            <div key={fracc.id} className="amenidades-config-card">
                                                <div className="amenidades-config-info">
                                                    <h3>{fracc.nombre || 'Sin nombre'}</h3>
                                                    <span className="amenidades-count">
                                                        {count === 1 ? '1 amenidad activa' : `${count} amenidades activas`}
                                                    </span>
                                                    <div className="amenidades-config-tags">
                                                        {amenityTags(amenidades)}
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="amenidades-config-button"
                                                    onClick={() => handleOpenDetail(fracc)}
                                                >
                                                    Configurar
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {view === 'detail' && selectedFraccionamiento && (
                    <>
                        <div className="amenidades-config-header detail page">
                            <div className="amenidades-config-header-left">
                                <button
                                    type="button"
                                    className="amenidades-config-back"
                                    onClick={handleCloseDetail}
                                    aria-label="Regresar al listado"
                                >
                                    <FaArrowLeft />
                                </button>
                                <div>
                                    <h2>{selectedFraccionamiento.nombre || 'Fraccionamiento'}</h2>
                                    <p>{selectedAmenities.length === 1 ? '1 amenidad activa' : `${selectedAmenities.length} amenidades activas`}</p>
                                </div>
                            </div>
                        </div>
                        <div className="amenidades-config-body">
                            <div className="amenidades-select-grid">
                                {AMENITIES_CATALOG.map((amenity) => {
                                    const Icon = amenity.Icon;
                                    const selected = selectedAmenities.includes(amenity.id);
                                    return (
                                        <button
                                            key={amenity.id}
                                            type="button"
                                            className={`amenidades-select-item ${selected ? 'selected' : ''}`}
                                            onClick={() => handleToggleAmenity(amenity.id)}
                                        >
                                            <span className="amenidades-select-icon">
                                                <Icon />
                                            </span>
                                            <span className="amenidades-select-title">{amenity.name}</span>
                                            {selected && <span className="amenidades-select-check">✓</span>}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="amenidades-config-actions">
                                <button type="button" className="amenidades-config-secondary" onClick={handleCloseDetail} disabled={saving}>
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    className="amenidades-config-primary"
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Guardando...' : 'Guardar selección'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ManagerConfiguracionAmenidades;
